import config from 'constants/config'
import FilterPanelLoader from 'components/Common/FilterPanel/FilterPanelCheckboxLoader'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { arrayToObject } from 'lib/array/arrayUtils'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import useMostPopulatedSearchExperienceFacets from '../hooks/useMostPopulatedSearchExperienceFacets'
import ExperienceSearchDateFilter from './ExperienceSearchDateFilter'
import ExperienceSearchFeaturedFilter from './ExperienceSearchFeaturedFilter'
import ExperienceSearchRatingFilter from './ExperienceSearchRatingFilter'
import ExperienceSearchPriceFilter from './ExperienceSearchPriceFilter'
import ExperienceSearchOnlyAtLuxuryEscapesFilter from './ExperienceSearchOnlyAtLuxuryEscapesFilter'
import ExperienceSearchSocieteFilters from './ExperienceSearchSocieteFilters'
import ExperienceSearchStartTimeFilter from './ExperienceSearchStartTimeFilter'
import ExperienceSearchAreaFilter from './ExperienceSearchAreaFilter'
import ExperienceSearchThemesFilter from './ExperienceSearchThemesFilter'

interface Props {
  place?: App.Place;
  listIds?: Array<string>;
  onAreaSelect?: () => void;
}

function ExperienceSearchPageFilters({ place, listIds, onAreaSelect }: Props) {
  const intl = useIntl()
  const {
    fetching,
    areas,
    ratingCounts,
    freeCancellationCount,
    instantConfirmationCount,
    giftingAvailableCount,
    timeSlotCounts,
  } = useMostPopulatedSearchExperienceFacets(place)

  const ratingCountsRecord = useMemo<Record<number, number>>(() => {
    return arrayToObject(
      ratingCounts,
      (item) => item.threshold,
      (item) => item.count,
    )
  }, [ratingCounts])

  const isLuxuryEscapesBrand = config.BRAND === 'luxuryescapes'

  const featuredCounts = useMemo<Record<Experiences.FeaturedFilter, number>>(
    () => ({
      'free-cancellation': freeCancellationCount,
      'instant-confirmation': instantConfirmationCount,
      'is-gift': giftingAvailableCount,
    }),
    [freeCancellationCount, instantConfirmationCount, giftingAvailableCount],
  )

  const timeSlotCountsRecord = useMemo<Partial<Record<Experiences.TimeSlotFilter, number>>>(() => {
    return arrayToObject(
      timeSlotCounts,
      (item) => item.slot,
      (item) => item.count,
    )
  }, [timeSlotCounts])

  if (fetching) {
    return (
      <VerticalSpacer gap={8}>
        <FilterPanelLoader type="checkbox" />
        <FilterPanelLoader type="slider" />
        {isLuxuryEscapesBrand && <FilterPanelLoader type="checkbox" />}
        <FilterPanelLoader type="checkbox" />
        <FilterPanelLoader type="checkbox" />
        <FilterPanelLoader type="checkbox" />
      </VerticalSpacer>
    )
  }

  return (
    <VerticalSpacer gap={8}>
      <FilterPanelFilterGroup
        compact
        title={intl.formatMessage({
          id: 'sYDWmqr07bNSasjyjqB6',
          defaultMessage: 'Time',
          description:
            'Title for the time filter group in experience search filters',
        })}
      >
        <ExperienceSearchDateFilter
          displayType="single-line"
          showNextBooking={false}
        />
      </FilterPanelFilterGroup>
      {isLuxuryEscapesBrand && (
        <FilterPanelFilterGroup
          compact
          title={intl.formatMessage({
            id: 'nVAvvHYd3ZaOgz_DOQsZ',
            defaultMessage: 'Recommended',
            description:
              'Title for the Luxury Escapes exclusive filter group in experience search filters',
          })}
        >
          <ExperienceSearchOnlyAtLuxuryEscapesFilter place={place} />
        </FilterPanelFilterGroup>
      )}
      <FilterPanelFilterGroup
        compact
        title={intl.formatMessage({
          id: 'm-B_-1T3rCIWIUZmz52H',
          defaultMessage: 'Themes',
          description:
            'Title for the themes filter group in experience search filters',
        })}
      >
        <ExperienceSearchThemesFilter place={place} />
      </FilterPanelFilterGroup>
      {!!areas.length && (
        <FilterPanelFilterGroup
          compact
          title={intl.formatMessage({
            id: 'dEI1iwsfXulfMvLbr624',
            defaultMessage: 'Areas',
            description: 'Title for the areas filter group in experience search filters',
          })}
        >
          <ExperienceSearchAreaFilter
            areas={areas}
            hideCounts
            onAreaSelect={onAreaSelect}
          />
        </FilterPanelFilterGroup>
      )}
      <FilterPanelFilterGroup
        compact
        title={intl.formatMessage({
          id: 'pebDNjLH-X6mkEWTrB3j',
          defaultMessage: 'Starting Time',
          description:
            'Title for the start time filter group in experience search filters',
        })}
      >
        <ExperienceSearchStartTimeFilter
          compact
          timeSlotCounts={timeSlotCountsRecord}
          hideCounts
        />
      </FilterPanelFilterGroup>
      <FilterPanelFilterGroup
        compact
        title={intl.formatMessage({
          id: 'uhzheYPeu42wS_U04j7E',
          defaultMessage: 'Price',
          description:
            'Title for the price filter group in experience search filters',
        })}
      >
        <ExperienceSearchPriceFilter place={place} listIds={listIds} />
      </FilterPanelFilterGroup>
      <ExperienceSearchSocieteFilters listIds={listIds} />
      <FilterPanelFilterGroup
        compact
        title={intl.formatMessage({
          id: '9eLIu9TMkOWSCYPks6rx',
          defaultMessage: 'Featured',
          description:
            'Title for the featured filter group in experience search filters',
        })}
      >
        <ExperienceSearchFeaturedFilter
          compact
          featuredCounts={featuredCounts}
          hideCounts
        />
      </FilterPanelFilterGroup>
      <FilterPanelFilterGroup
        compact
        title={intl.formatMessage({
          id: 'XGkSHbOIT8muMeKg7j0c',
          defaultMessage: 'Customer rating',
          description:
            'Title for the rating filter group in experience search filters',
        })}
      >
        <ExperienceSearchRatingFilter
          compact
          ratingCounts={ratingCountsRecord}
          hideCounts
        />
      </FilterPanelFilterGroup>
    </VerticalSpacer>
  )
}

export default ExperienceSearchPageFilters
